export type DifficultyId = "easy" | "medium" | "hard";

export type DifficultyDef = {
  id: DifficultyId;
  label: string;
  /** Short tag under the button */
  hint: string;
  baseSpeed: number;
  speedRampPerSec: number;
  speedPerChunk: number;
  chunkMeters: number;
  maxSpeed: number;
  hazardSpawnMin: number;
  hazardSpawnMax: number;
  coinSpawnChance: number;
  /** Chance to spawn a jump token when a hazard wave spawns. */
  jumpSpawnChance: number;
  rivalSpawnChance: number;
  doubleLaneBias: number;
  coinRewardMult: number;
  /**
   * How fast rivals drive along the road (same direction as the player).
   * Approach speed becomes `playerSpeed - rivalDriveSpeed` — 0 = static on track.
   */
  rivalDriveSpeed: number;
  /** Runs completed before this difficulty unlocks (0 = always). */
  unlockRuns: number;
};

export const DIFFICULTIES: Record<DifficultyId, DifficultyDef> = {
  easy: {
    id: "easy",
    label: "Easy",
    hint: "Chill",
    baseSpeed: 9,
    speedRampPerSec: 0.28,
    speedPerChunk: 0.65,
    chunkMeters: 60,
    maxSpeed: 22,
    hazardSpawnMin: 0.95,
    hazardSpawnMax: 1.55,
    coinSpawnChance: 0.55,
    jumpSpawnChance: 0.28,
    rivalSpawnChance: 0.1,
    doubleLaneBias: 0.18,
    coinRewardMult: 0.75,
    rivalDriveSpeed: 0,
    unlockRuns: 0,
  },
  medium: {
    id: "medium",
    label: "Medium",
    hint: "Arcade",
    baseSpeed: 12,
    speedRampPerSec: 0.55,
    speedPerChunk: 0.9,
    chunkMeters: 48,
    maxSpeed: 32,
    hazardSpawnMin: 0.65,
    hazardSpawnMax: 1.15,
    coinSpawnChance: 0.42,
    jumpSpawnChance: 0.22,
    rivalSpawnChance: 0.2,
    doubleLaneBias: 0.4,
    coinRewardMult: 1,
    rivalDriveSpeed: 0,
    unlockRuns: 3,
  },
  hard: {
    id: "hard",
    label: "Hard",
    hint: "Insane",
    baseSpeed: 16,
    speedRampPerSec: 0.75,
    speedPerChunk: 1.15,
    chunkMeters: 35,
    maxSpeed: 42,
    hazardSpawnMin: 0.42,
    hazardSpawnMax: 0.9,
    coinSpawnChance: 0.32,
    jumpSpawnChance: 0.18,
    rivalSpawnChance: 0.3,
    doubleLaneBias: 0.5,
    coinRewardMult: 1.4,
    /** Slow traffic on their lane — readable, not instant wall. */
    rivalDriveSpeed: 7.5,
    unlockRuns: 12,
  },
};

export const DIFFICULTY_LIST = Object.values(DIFFICULTIES);

/** 0..1 progress toward max speed for HUD. */
export function speedProgress(diff: DifficultyDef, speed: number) {
  const span = Math.max(0.05, diff.maxSpeed - diff.baseSpeed);
  return Math.min(1, Math.max(0, (speed - diff.baseSpeed) / span));
}

/** Discrete gear 1..5 for “speed up” feedback. */
export function speedGear(diff: DifficultyDef, speed: number) {
  const p = speedProgress(diff, speed);
  return Math.min(5, 1 + Math.floor(p * 4.999));
}

export function isDifficultyUnlocked(id: DifficultyId, gamesPlayed: number) {
  return gamesPlayed >= DIFFICULTIES[id].unlockRuns;
}

export function runSpeed(diff: DifficultyDef, elapsedSec: number, distance: number) {
  const fromTime = diff.baseSpeed + diff.speedRampPerSec * elapsedSec;
  const chunks = Math.floor(distance / diff.chunkMeters);
  const fromDist = chunks * diff.speedPerChunk;
  return Math.min(diff.maxSpeed, fromTime + fromDist);
}
